import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { DownloadSimple, CreditCard, Warning, CheckCircle, SpinnerGap, Receipt } from '@phosphor-icons/react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(n || 0);

export default function KlantPortalPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showing, setShowing] = useState('alles'); // alles | openstaand | betaald

  useEffect(() => {
    axios.get(`${BACKEND}/api/portal/${token}`)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || 'Geen toegang — controleer uw link'))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <SpinnerGap size={48} className="animate-spin text-[#002FA7]" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="bg-white p-8 border-l-4 border-red-500 shadow-lg max-w-md text-center">
        <Warning size={48} className="text-red-500 mx-auto mb-3" weight="bold" />
        <h1 className="text-xl font-bold text-gray-800 mb-2">Toegang geweigerd</h1>
        <p className="text-sm text-gray-600">{error}</p>
        <p className="text-xs text-gray-400 mt-4">Neem contact op met uw leverancier voor een nieuwe link.</p>
      </div>
    </div>
  );

  const facturen = data.facturen || [];
  const open = facturen.filter(f => !f.betaald);
  const betaald = facturen.filter(f => f.betaald);
  const toon = showing === 'openstaand' ? open : (showing === 'betaald' ? betaald : facturen);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="klant-portaal">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Klantenportaal</div>
            <h1 className="text-xl font-bold text-[#002FA7]">{data.leverancier.naam}</h1>
          </div>
          <div className="text-right text-xs text-gray-500">
            <div>Ingelogd als</div>
            <div className="font-bold text-gray-800">{data.klant.naam}</div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-5">
        {/* Saldo card */}
        <div className={`p-6 border-l-4 ${data.openstaand_totaal > 0 ? 'bg-red-50 border-red-500' : 'bg-green-50 border-green-500'}`} data-testid="saldo-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Openstaand saldo</div>
              <div className={`text-4xl font-black ${data.openstaand_totaal > 0 ? 'text-red-700' : 'text-green-700'}`}>
                {fmt(data.openstaand_totaal)}
              </div>
              <div className="text-sm text-gray-600">
                {data.aantal_openstaand > 0
                  ? `${data.aantal_openstaand} openstaande factu${data.aantal_openstaand > 1 ? 'ren' : 'ur'}`
                  : 'Geen openstaande facturen — dank u voor uw stiptheid! 🙏'}
              </div>
            </div>
            {data.leverancier.iban && (
              <div className="text-xs text-gray-500 text-right">
                <div>Rekeningnummer</div>
                <div className="font-mono font-bold text-gray-800">{data.leverancier.iban}</div>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 border-b border-gray-200">
          <button className={`text-sm font-bold pb-2 -mb-[1px] border-b-2 ${showing === 'alles' ? 'border-[#002FA7] text-[#002FA7]' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
            onClick={() => setShowing('alles')} data-testid="tab-alles">
            Alle facturen ({facturen.length})
          </button>
          <button className={`text-sm font-bold pb-2 -mb-[1px] border-b-2 ${showing === 'openstaand' ? 'border-[#002FA7] text-[#002FA7]' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
            onClick={() => setShowing('openstaand')} data-testid="tab-openstaand">
            Openstaand ({open.length})
          </button>
          <button className={`text-sm font-bold pb-2 -mb-[1px] border-b-2 ${showing === 'betaald' ? 'border-green-600 text-green-700' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
            onClick={() => setShowing('betaald')} data-testid="tab-betaald">
            Betaald ({betaald.length})
          </button>
        </div>

        {/* Facturen tabel */}
        <div className="bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-[10px] uppercase tracking-wider text-gray-500 font-bold">Nummer</th>
                <th className="text-left px-4 py-3 text-[10px] uppercase tracking-wider text-gray-500 font-bold">Datum</th>
                <th className="text-left px-4 py-3 text-[10px] uppercase tracking-wider text-gray-500 font-bold">Vervaldag</th>
                <th className="text-right px-4 py-3 text-[10px] uppercase tracking-wider text-gray-500 font-bold">Totaal</th>
                <th className="text-right px-4 py-3 text-[10px] uppercase tracking-wider text-gray-500 font-bold">Te betalen</th>
                <th className="text-center px-4 py-3 text-[10px] uppercase tracking-wider text-gray-500 font-bold">Status</th>
                <th className="text-center px-4 py-3 text-[10px] uppercase tracking-wider text-gray-500 font-bold">Actie</th>
              </tr>
            </thead>
            <tbody>
              {toon.length === 0 && (
                <tr><td colSpan="7" className="text-center text-gray-400 py-12">
                  <Receipt size={32} weight="thin" className="mx-auto mb-2 text-gray-300" />
                  Geen {showing === 'openstaand' ? 'openstaande' : ''} facturen
                </td></tr>
              )}
              {toon.map(f => (
                <tr key={f.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`row-${f.id}`}>
                  <td className="px-4 py-3 font-mono font-bold text-[#002FA7]">{f.factuurnummer}</td>
                  <td className="px-4 py-3 text-gray-600">{f.datum}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {f.vervaldag || '-'}
                    {f.vervallen && <span className="ml-1.5 text-[9px] font-bold px-1.5 py-0.5 bg-red-100 text-red-700">VERVALLEN</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-bold">{fmt(f.totaal)}</td>
                  <td className="px-4 py-3 text-right font-bold">{f.betaald ? <span className="text-green-600">€ 0,00</span> : <span className="text-red-600">{fmt(f.te_betalen)}</span>}</td>
                  <td className="px-4 py-3 text-center">
                    {f.betaald ? (
                      <span className="text-[10px] font-bold px-2 py-0.5 bg-green-600 text-white"><CheckCircle size={10} weight="bold" className="inline" /> Betaald</span>
                    ) : f.vervallen ? (
                      <span className="text-[10px] font-bold px-2 py-0.5 bg-red-500 text-white">Vervallen</span>
                    ) : (
                      <span className="text-[10px] font-bold px-2 py-0.5 bg-blue-100 text-blue-800">Te betalen</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex gap-1 justify-center">
                      {!f.betaald && f.stripe_link && (
                        <a href={f.stripe_link} target="_blank" rel="noopener noreferrer"
                           className="inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 bg-[#635BFF] text-white hover:bg-[#4F46E5]"
                           data-testid={`pay-${f.id}`}>
                          <CreditCard size={11} weight="bold" /> Betaal
                        </a>
                      )}
                      <button className="inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 bg-gray-100 text-gray-700 hover:bg-gray-200"
                        onClick={() => window.open(`${BACKEND}/api/portal/${token}/factuur/${f.id}/pdf`, '_blank')}
                        data-testid={`view-${f.id}`}>
                        <DownloadSimple size={11} weight="bold" /> Bekijk
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Contact footer */}
        <div className="bg-white p-5 border-l-4 border-[#002FA7] text-xs text-gray-600">
          <div className="font-bold text-gray-800 mb-2">Vragen over een factuur?</div>
          <div>Neem contact op met <strong>{data.leverancier.naam}</strong>
            {data.leverancier.email && <> — <a href={`mailto:${data.leverancier.email}`} className="text-[#002FA7] underline">{data.leverancier.email}</a></>}
            {data.leverancier.tel && <> — {data.leverancier.tel}</>}
          </div>
        </div>

        <div className="text-center text-[10px] text-gray-400 py-4">
          Veilige toegang — uw persoonlijke link. Deel deze niet met anderen.
        </div>
      </div>
    </div>
  );
}
